import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import omit from 'lodash.omit';
import { Alert } from '@m/alchemy-ui/Alert';
import { Button } from '@m/alchemy-ui/Button';
import { Checkbox } from '@m/alchemy-ui/Checkbox';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { FieldStatus } from '@m/alchemy-ui/FieldStatus';
import { Toast } from '@m/alchemy-ui/Toast';
import { Form, Formik, type FormikErrors, useFormikContext } from 'formik';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { DEFAULT_LANGUAGE } from '../shared-constants';
import { SingleUpload } from '../../common/components';
import { FileUploadType, type ToastMessageType } from '../../common/enums';
import { type File } from '../../common/core-api';
import { type Translation } from '../../common/interfaces';
import { getConfirmToastMessage, getErrorToastMessage } from '../../helpers';
import { useFile } from '../../hooks';
import { GET_PAGE_CMS_EDITOR_DATA, type PageCmsEditorDataQuery } from '../../graphql/queries';
import { DELETE_PAGE, EDIT_CUSTOM_PAGE, SET_PAGE_PUBLICATION_STATUS } from '../../graphql/mutations';
import {
  type DeletePageMutation,
  type DeletePageMutationVariables,
  type EditCustomPageMutation,
  type EditCustomPageMutationVariables,
  type SetPagePublicationStatusMutation,
  type SetPagePublicationStatusMutationVariables
} from '../../graphql/mutations/__generated__/page.generated';
import { type EditCustomPageProps } from '../../features';
import { PanelEditorDataContext } from './common/panel-editor-data';
import { normalizePanelSizes } from './common/panel-grid';
import { type PageCmsPanels } from './common/panel-types';
import { panelsHaveErrors, panelValidationMessages } from './common/panel-validation';
import { normalizeVideoEmbedCode } from './common/video-embed';
import { panelEditorMessages } from './components/panel-editors/panel-editor-messages';
import { buildPanelsSavePayload, buildTranslationsSavePayload } from './common/save-payload';
import { updateNavigationItem } from './common/update-navigation-item';
import { AddElementMenu } from './components/AddElementMenu';
import { PanelGridLayout } from './components/PanelGridLayout';
import { customPageTitleMessages, CustomPageTitleRow } from './components/CustomPageTitleRow';
import {
  BannerRow,
  DeletePageRow,
  DesignTabInfo,
  DesignTabLink,
  ErrorBannerRow,
  GridArea,
  IntroText,
  PanelErrorStatus,
  PreviewText,
  SecondaryButton,
  StyledLoadingIndicator,
  Toolbar,
  ToolbarCenter,
  ToolbarLeft,
  ToolbarRight,
  Wrapper
} from './CustomPage.styled';

const DESIGN_TAB_URL = '/design/admin_viewdesign';
const ADMIN_PAGES_URL = '/page/admin_view';

const messages = defineMessages({
  intro: {
    id: 'newCustomPageIntro',
    defaultMessage:
      'Custom pages can display elements with text, carousels, images and activity lists in any order you like.'
  },
  preview: {
    id: 'editCustomPagePreview',
    defaultMessage: 'Preview'
  },
  savePage: {
    id: 'editCustomPageSavePage',
    defaultMessage: 'Save Page'
  },
  publish: {
    id: 'publish',
    defaultMessage: 'Publish'
  },
  unpublish: {
    id: 'unpublish',
    defaultMessage: 'Unpublish'
  },
  pagePublished: {
    id: 'pagePublished',
    defaultMessage: 'Page published'
  },
  pageUnpublished: {
    id: 'pageUnpublished',
    defaultMessage: 'Page unpublished'
  },
  useDifferentBanner: {
    id: 'editCustomPageUseDifferentBanner',
    defaultMessage: 'Use a different banner'
  },
  attachImage: {
    id: 'attachImage',
    defaultMessage: 'Attach image'
  },
  bannerUploadInstructions: {
    id: 'editCustomPageBannerUploadInstructions',
    defaultMessage: 'Drag & Drop or manually attach an image'
  },
  bannerUploadDimensions: {
    id: 'editCustomPageBannerUploadDimensions',
    defaultMessage: 'We recommend that your banner is 980 pixels wide and 124 pixels high.'
  },
  designTabLinkText: {
    id: 'editCustomPageDesignTabLink',
    defaultMessage: 'Design tab'
  },
  deletePage: {
    id: 'editCustomPageDeletePage',
    defaultMessage: 'Delete page'
  },
  deletePageConfirmation: {
    id: 'editCustomPageDeletePageConfirmation',
    defaultMessage: 'This action cannot be undone.'
  },
  deleteFailed: {
    id: 'deleteFail',
    defaultMessage: 'Delete failed'
  },
  panelErrorsBanner: {
    id: 'editCustomPagePanelErrorsBanner',
    defaultMessage: 'Please correct the errors below.'
  },
  saveSuccess: {
    id: 'saveSuccess',
    defaultMessage: 'Save successful'
  },
  saveFailed: {
    id: 'saveFail',
    defaultMessage: 'Save failed'
  }
});

interface EditPageFormValues {
  language: string;
  pageNavTitle: string;
  isExclude: boolean;
  isHidden: boolean;
  useDifferentBanner: boolean;
  bannerFile: File | null;
  translations: Translation[];
  panels: PageCmsPanels;
}

/*
 * Stored video embed code can be in legacy shapes the client-side parser rejects (see
 * normalizeVideoEmbedCode), which would falsely flag untouched panels as invalid and block Save.
 * Healing it here, before the values reach Formik, means validation and the preview are correct
 * from the first render.
 */
const normalizePanels = (panels: PageCmsPanels, videoFallbackText: string): PageCmsPanels =>
  normalizePanelSizes(
    panels.map((row) =>
      row.map((panel) =>
        panel.settings.__typename === 'VideoPanelSettings'
          ? {
              ...panel,
              settings: {
                ...panel.settings,
                videoEmbedCode: normalizeVideoEmbedCode(panel.settings, videoFallbackText)
              }
            }
          : panel
      )
    )
  );

const buildInitialValues = (
  page: NonNullable<PageCmsEditorDataQuery['pageCMS']> | null | undefined,
  videoFallbackText: string
): EditPageFormValues => ({
  language: DEFAULT_LANGUAGE,
  pageNavTitle: page?.pageNavTitle ?? '',
  isExclude: Boolean(page?.isExclude),
  isHidden: Boolean(page?.isHidden),
  useDifferentBanner: Boolean(page?.bannerFile),
  bannerFile: page?.bannerFile ?? null,
  translations: (page?.translations ?? [])
    .filter((translation): translation is NonNullable<typeof translation> => translation !== null)
    .map((translation) => ({
      id: translation.id,
      communityId: translation.communityId,
      field: translation.field,
      iso: translation.iso,
      // Align objectId with the field-based translate id used by TranslatableInput.
      objectId: translation.field,
      objectType: translation.objectType,
      value: translation.value
    })),
  panels: normalizePanels(page?.panels ?? [], videoFallbackText)
});

const bannerImageMissing = (values: EditPageFormValues) => values.useDifferentBanner && !values.bannerFile;

/*
 * The form-level error banner: armed by a save attempt with invalid panels or a missing banner
 * image, gone as soon as everything is fixed (validate reruns on change and clears the errors).
 */
const SaveErrorsBanner = ({ showSaveErrors }: { readonly showSaveErrors: boolean }) => {
  const intl = useIntl();
  const { errors } = useFormikContext<EditPageFormValues>();

  if (!showSaveErrors || (typeof errors.panels !== 'string' && typeof errors.bannerFile !== 'string')) {
    return null;
  }

  return (
    <ErrorBannerRow>
      <Alert level="error" value={intl.formatMessage(messages.panelErrorsBanner)} />
    </ErrorBannerRow>
  );
};

/*
 * Disarms the save-error display once every panel and the banner are fixed, giving a clean slate:
 * breaking something again after that shows no error until Save is pressed again.
 */
const SaveErrorsWatcher = ({
  showSaveErrors,
  onCleared
}: {
  readonly showSaveErrors: boolean;
  readonly onCleared: () => void;
}) => {
  const { values } = useFormikContext<EditPageFormValues>();

  useEffect(() => {
    if (showSaveErrors && !panelsHaveErrors(values.panels) && !bannerImageMissing(values)) {
      onCleared();
    }
  }, [showSaveErrors, values, onCleared]);

  return null;
};

export const EditCustomPage = ({ pageId, community }: EditCustomPageProps) => {
  const intl = useIntl();
  const [, uploadExtensions] = useFile();

  // Armed on Save when panels or the banner are invalid, disarmed by SaveErrorsWatcher once all are fixed
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const handleSaveErrorsCleared = useCallback(() => setShowSaveErrors(false), []);

  const [toastMessages, setToastMessages] = useState<ToastMessageType[]>([]);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [editCustomPage, { loading: saving }] = useMutation<EditCustomPageMutation, EditCustomPageMutationVariables>(
    EDIT_CUSTOM_PAGE
  );

  const [deletePage] = useMutation<DeletePageMutation, DeletePageMutationVariables>(DELETE_PAGE);

  const [setPagePublicationStatus, { loading: publishing }] = useMutation<
    SetPagePublicationStatusMutation,
    SetPagePublicationStatusMutationVariables
  >(SET_PAGE_PUBLICATION_STATUS, {
    // Keep the result out of the normalised cache: a Page:{id} update would re-emit the editor
    // query, rebuild initialValues, and enableReinitialize would reset the form — discarding
    // every unsaved edit. handlePublishToggle applies the returned status via setFieldValue.
    fetchPolicy: 'no-cache'
  });

  const { data: pageCmsEditorData, loading } = useQuery<PageCmsEditorDataQuery>(GET_PAGE_CMS_EDITOR_DATA, {
    variables: { pageId: Number(pageId), communityId: Number(community.id) }
  });

  const requiredMessage = intl.formatMessage(customPageTitleMessages.requiredTitleError);
  const panelErrorsMessage = intl.formatMessage(messages.panelErrorsBanner);
  const bannerImageRequiredMessage = intl.formatMessage(panelValidationMessages.imageRequired);

  const closeDeleteConfirm = useCallback(() => setDeleteConfirmOpen(false), []);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await deletePage({ variables: { id: Number(pageId) } });
      window.location.href = ADMIN_PAGES_URL;
    } catch {
      setDeleteConfirmOpen(false);
      setToastMessages(getErrorToastMessage(intl.formatMessage(messages.deleteFailed)));
    }
  }, [deletePage, pageId, intl]);

  const videoFallbackText = intl.formatMessage(panelEditorMessages.browserDoesNotSupportVideo);

  const initialValues = useMemo(
    () => buildInitialValues(pageCmsEditorData?.pageCMS, videoFallbackText),
    [pageCmsEditorData, videoFallbackText]
  );

  // Link-target picker data for the text/image panel editors (LinkConfigSelect).
  const panelEditorData = useMemo(
    () => ({
      challenges: pageCmsEditorData?.challenges ?? [],
      pages: (pageCmsEditorData?.pages ?? []).filter((page): page is NonNullable<typeof page> => page !== null)
    }),
    [pageCmsEditorData]
  );

  const validate = useCallback(
    (values: EditPageFormValues) => {
      const errors: FormikErrors<EditPageFormValues> = {};
      if (!values.pageNavTitle.trim()) {
        errors.pageNavTitle = requiredMessage;
      }
      if (panelsHaveErrors(values.panels)) {
        errors.panels = panelErrorsMessage;
      }
      if (bannerImageMissing(values)) {
        errors.bannerFile = bannerImageRequiredMessage;
      }
      return errors;
    },
    [requiredMessage, panelErrorsMessage, bannerImageRequiredMessage]
  );

  return (
    <Wrapper data-testid="edit-page-view">
      {loading ? (
        <StyledLoadingIndicator />
      ) : (
        <PanelEditorDataContext.Provider value={panelEditorData}>
          <IntroText>{intl.formatMessage(messages.intro)}</IntroText>
          <Formik<EditPageFormValues>
            enableReinitialize
            initialValues={initialValues}
            validate={validate}
            onSubmit={async () => undefined}
          >
            {({ values, setFieldValue }) => {
              const handleSaveClick = async () => {
                const hasPanelErrors = panelsHaveErrors(values.panels);
                const hasBannerError = bannerImageMissing(values);
                setShowSaveErrors(hasPanelErrors || hasBannerError);
                if (hasPanelErrors || hasBannerError || !values.pageNavTitle.trim()) {
                  return;
                }

                try {
                  const { data } = await editCustomPage({
                    variables: {
                      id: Number(pageId),
                      input: {
                        pageNavTitle: values.pageNavTitle,
                        isExclude: values.isExclude,
                        bannerFileId: values.useDifferentBanner ? (values.bannerFile?.id ?? null) : null,
                        panels: buildPanelsSavePayload(values.panels),
                        translations: buildTranslationsSavePayload(values.translations).map((translation) =>
                          omit(translation, 'communityId')
                        )
                      }
                    }
                  });
                  const page = data?.editCustomPage;
                  if (page) {
                    updateNavigationItem(pageId, {
                      isHiddenInNavigation: Boolean(page.isHidden) || Boolean(page.isExclude),
                      navTitle: page.pageNavTitle
                    });
                  }
                  setToastMessages(getConfirmToastMessage(intl.formatMessage(messages.saveSuccess)));
                } catch {
                  setToastMessages(getErrorToastMessage(intl.formatMessage(messages.saveFailed)));
                }
              };

              const handlePublishToggle = async () => {
                try {
                  const { data } = await setPagePublicationStatus({
                    variables: { id: Number(pageId), isPublished: values.isHidden }
                  });
                  const page = data?.setPagePublicationStatus;
                  if (!page) {
                    throw new Error('No page returned');
                  }
                  setFieldValue('isHidden', Boolean(page.isHidden));
                  updateNavigationItem(pageId, {
                    isHiddenInNavigation: Boolean(page.isHidden) || values.isExclude
                  });
                  setToastMessages(
                    getConfirmToastMessage(
                      intl.formatMessage(page.isHidden ? messages.pageUnpublished : messages.pagePublished)
                    )
                  );
                } catch {
                  setToastMessages(getErrorToastMessage(intl.formatMessage(messages.saveFailed)));
                }
              };

              const handleDeleteClick = () => {
                setDeleteConfirmOpen(true);
              };

              return (
                <Form>
                  <SaveErrorsWatcher showSaveErrors={showSaveErrors} onCleared={handleSaveErrorsCleared} />

                  <SaveErrorsBanner showSaveErrors={showSaveErrors} />

                  <Toast messages={toastMessages} />

                  <CustomPageTitleRow />

                  <Toolbar id="admin_page_instance_toolbar">
                    <ToolbarLeft>
                      <AddElementMenu />
                    </ToolbarLeft>

                    <ToolbarCenter>
                      <PreviewText>{intl.formatMessage(messages.preview)}</PreviewText>
                    </ToolbarCenter>

                    <ToolbarRight>
                      {values.isHidden ? (
                        <Button type="button" priority="primary" isLoading={publishing} onClick={handlePublishToggle}>
                          {intl.formatMessage(messages.publish)}
                        </Button>
                      ) : (
                        <SecondaryButton
                          type="button"
                          priority="secondary"
                          isLoading={publishing}
                          onClick={handlePublishToggle}
                        >
                          {intl.formatMessage(messages.unpublish)}
                        </SecondaryButton>
                      )}
                      <Button type="submit" priority="primary" isLoading={saving} onClick={handleSaveClick}>
                        {intl.formatMessage(messages.savePage)}
                      </Button>
                    </ToolbarRight>
                  </Toolbar>

                  <BannerRow>
                    <Checkbox
                      label={intl.formatMessage(messages.useDifferentBanner)}
                      checked={values.useDifferentBanner}
                      onChange={(event) => setFieldValue('useDifferentBanner', event.target.checked)}
                    />

                    {values.useDifferentBanner && showSaveErrors && !values.bannerFile && (
                      <PanelErrorStatus>
                        <FieldStatus level="error" message={bannerImageRequiredMessage} />
                      </PanelErrorStatus>
                    )}

                    {values.useDifferentBanner && (
                      <SingleUpload
                        buttonLabel={intl.formatMessage(messages.attachImage)}
                        fileTypes={uploadExtensions.image}
                        type={FileUploadType.PAGE_HEADER}
                        labelId="custom-page-banner-upload"
                        describedBy="custom-page-banner-upload-instructions"
                        currentFile={values.bannerFile}
                        onFileUpload={(file) => setFieldValue('bannerFile', file)}
                        onFileDelete={() => setFieldValue('bannerFile', null)}
                      >
                        <span id="custom-page-banner-upload-instructions">
                          <p>{intl.formatMessage(messages.bannerUploadInstructions)}</p>
                          <p>{intl.formatMessage(messages.bannerUploadDimensions)}</p>
                        </span>
                      </SingleUpload>
                    )}
                  </BannerRow>

                  <DesignTabInfo>
                    <FormattedMessage
                      id="editCustomPageDesignTabInfo"
                      defaultMessage="By default your page will use the community banner set in the {designTab}."
                      values={{
                        designTab: (
                          <DesignTabLink href={DESIGN_TAB_URL}>
                            {intl.formatMessage(messages.designTabLinkText)}
                          </DesignTabLink>
                        )
                      }}
                    />
                  </DesignTabInfo>

                  <GridArea>
                    <PanelGridLayout showPanelErrors={showSaveErrors} />
                  </GridArea>

                  <DeletePageRow>
                    <SecondaryButton type="button" priority="secondary" onClick={handleDeleteClick}>
                      {intl.formatMessage(messages.deletePage)}
                    </SecondaryButton>
                  </DeletePageRow>

                  <Dialog
                    isOpen={deleteConfirmOpen}
                    onClose={closeDeleteConfirm}
                    headerContent={intl.formatMessage({ id: 'areYouSure', defaultMessage: 'Are you sure?' })}
                    bodyContent={intl.formatMessage(messages.deletePageConfirmation)}
                    footerButtons={[
                      {
                        label: intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' }),
                        onClick: closeDeleteConfirm,
                        priority: 'tertiary'
                      },
                      {
                        label: intl.formatMessage({ id: 'confirm', defaultMessage: 'Confirm' }),
                        onClick: () => {
                          handleConfirmDelete();
                        },
                        destructive: true
                      }
                    ]}
                  />
                </Form>
              );
            }}
          </Formik>
        </PanelEditorDataContext.Provider>
      )}
    </Wrapper>
  );
};
